import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, HelpCircle, Tv, Users, TrendingUp, Heart, Zap, Shield } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import api from '../services/api';

const FAQ = () => {
  const [openQuestion, setOpenQuestion] = useState(null);
  // Tarifs live (jamais de montant codé en dur) — valeurs de repli = dernier tarif connu si l'API échoue.
  const [pricing, setPricing] = useState({ fromPrice: 12, yearPrice: 59, yearMonthly: 4.9 });

  useEffect(() => {
    api.get('/pricing-matrix').then((r) => {
      const offers = r.data?.offers || [];
      if (!offers.length) return;
      const fromPrice = Math.min(...offers.map((o) => o.from_price));
      const yearOffer = offers.find((o) => o.key === '1year') || offers[offers.length - 1];
      if (yearOffer) {
        setPricing({
          fromPrice,
          yearPrice: yearOffer.from_price,
          yearMonthly: yearOffer.from_monthly,
        });
      }
    }).catch(() => {});
  }, []);

  const toggleQuestion = (index) => {
    setOpenQuestion(openQuestion === index ? null : index);
  };

  const faqCategories = [
    {
      icon: <Tv className="h-6 w-6" />,
      title: 'Questions générales',
      color: '#FF6B35',
      questions: [
        {
          q: "Qu'est-ce qu'IPTV4KFR ?",
          a: "IPTV4KFR est une solution de divertissement complète qui permet d'accéder à un large catalogue de chaînes TV, de sports, de films et de séries, sur tous vos écrans, avec un seul abonnement annuel. C'est la solution idéale pour toute la famille."
        },
        {
          q: "Que comprend l'abonnement ?",
          a: (
            <div>
              <p className="mb-3">Un abonnement IPTV4KFR inclut :</p>
              <ul className="space-y-2 ml-4">
                <li className="flex items-start space-x-2">
                  <span className="text-[#00FF88] mt-1">✓</span>
                  <span>Plus de 15 000 chaînes TV françaises et internationales</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#00FF88] mt-1">✓</span>
                  <span>Tous les sports en direct, dont le football, la F1, le tennis...</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#00FF88] mt-1">✓</span>
                  <span>Un large choix de films et séries à la demande</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#00FF88] mt-1">✓</span>
                  <span>Accès multi-écrans pour toute la famille</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#00FF88] mt-1">✓</span>
                  <span>Un espace client dédié pour gérer votre compte</span>
                </li>
              </ul>
              <p className="mt-3 text-[#FFB800] font-semibold">Le tout avec un fonctionnement simple et centralisé, à un prix accessible.</p>
            </div>
          )
        },
        {
          q: "Pourquoi choisir IPTV4KFR plutôt que plusieurs abonnements séparés ?",
          a: (
            <div>
              <p className="mb-3">Avec IPTV4KFR, vous économisez considérablement :</p>
              <div className="bg-[#FFB800]/10 border border-[#FFB800]/30 p-4 rounded-lg mb-3">
                <p className="text-[#FFB800] font-bold mb-2">💰 Comparaison économique :</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Canal+ Premium</span>
                    <span className="text-[#FF3B5C]">~40€/mois</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Netflix + Prime Video</span>
                    <span className="text-[#FF3B5C]">~25€/mois</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Autres plateformes</span>
                    <span className="text-[#FF3B5C]">~20€/mois</span>
                  </div>
                  <div className="border-t border-[#FFB800]/30 pt-2 mt-2 flex justify-between font-bold">
                    <span>Total mensuel</span>
                    <span className="text-[#FF3B5C]">~85€/mois</span>
                  </div>
                  <div className="border-t border-[#00FF88]/30 pt-2 mt-2 flex justify-between font-bold text-lg">
                    <span className="text-white">IPTV4KFR (1 an)</span>
                    <span className="text-[#00FF88]">{pricing.yearPrice}€/an (soit {pricing.yearMonthly}€/mois)</span>
                  </div>
                </div>
              </div>
              <p className="text-[#00FF88] font-semibold">Soit plus de 1 000€ d'économies par an pour un contenu bien plus riche !</p>
            </div>
          )
        },
        {
          q: "L'abonnement est-il compliqué à utiliser ?",
          a: "Non. IPTV4KFR est conçu pour être accessible à tous, même sans connaissances techniques. Des tutoriels clairs sont fournis et notre support vous accompagne si nécessaire."
        },
        {
          q: "Sur quels appareils puis-je utiliser IPTV4KFR ?",
          a: (
            <div>
              <p className="mb-3">Vous pouvez utiliser IPTV4KFR sur :</p>
              <ul className="space-y-2 ml-4">
                <li className="flex items-center space-x-2">
                  <span className="text-[#FF6B35]">📺</span>
                  <span>Smart TV (Samsung, LG, Android TV)</span>
                </li>
                <li className="flex items-center space-x-2">
                  <span className="text-[#00FF88]">📦</span>
                  <span><strong>Boîtier Android</strong> (solution recommandée)</span>
                </li>
                <li className="flex items-center space-x-2">
                  <span className="text-[#FFB800]">🔌</span>
                  <span><strong>Clé HDMI Android TV</strong> (solution recommandée)</span>
                </li>
                <li className="flex items-center space-x-2">
                  <span className="text-[#FF6B9D]">📱</span>
                  <span>Smartphone et tablette (Android & iOS)</span>
                </li>
              </ul>
              <p className="mt-3 text-[#B8B8BA] text-sm">👉 La solution la plus simple et stable reste le boîtier ou la clé HDMI Android.</p>
            </div>
          )
        },
        {
          q: "Puis-je regarder sur plusieurs appareils en même temps ?",
          a: "Oui. IPTV4KFR permet un usage multi-écrans pour toute la famille. Les conditions d'utilisation sont précisées dans votre espace client."
        }
      ]
    },
    {
      icon: <Zap className="h-6 w-6" />,
      title: 'Installation & utilisation',
      color: '#F7931E',
      questions: [
        {
          q: "L'installation est-elle difficile ?",
          a: (
            <div>
              <p className="mb-3">Non. Dans la majorité des cas :</p>
              <ul className="space-y-2 ml-4">
                <li className="flex items-start space-x-2">
                  <span className="text-[#00FF88]">✓</span>
                  <span>L'installation prend quelques minutes</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#00FF88]">✓</span>
                  <span>Aucune configuration complexe requise</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#00FF88]">✓</span>
                  <span>Les identifiants fournis sont compatibles avec la plupart des applications IPTV</span>
                </li>
              </ul>
              <p className="mt-3">Des guides pas à pas sont disponibles dans la <Link to="/tutoriels" className="text-[#FF6B35] hover:underline">section Tutoriels et assistance</Link>.</p>
            </div>
          )
        },
        {
          q: "Quelle application dois-je utiliser ?",
          a: (
            <div>
              <p className="mb-3">Nous recommandons :</p>
              <ul className="space-y-2 ml-4">
                <li className="flex items-start space-x-2">
                  <span className="text-[#FF6B35]">•</span>
                  <span>Des <strong>applications partenaires</strong></span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#FF6B35]">•</span>
                  <span>Ou des applications optimisées fournies par nos prestataires</span>
                </li>
              </ul>
              <div className="mt-3 bg-[#FFB800]/10 border border-[#FFB800]/30 p-3 rounded-lg">
                <p className="text-[#FFB800] text-sm">ℹ️ Certaines applications peuvent nécessiter un accès payant indépendant (ex. licence à vie), clairement indiqué dans les tutoriels.</p>
              </div>
            </div>
          )
        },
        {
          q: "Que faire si je ne suis pas à l'aise avec la technique ?",
          a: (
            <div>
              <p className="mb-3">Nous vous orientons vers :</p>
              <ul className="space-y-2 ml-4">
                <li className="flex items-start space-x-2">
                  <span className="text-[#00FF88]">✓</span>
                  <span>La solution la plus simple selon votre équipement</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#00FF88]">✓</span>
                  <span>Ou vers un <strong>boîtier / clé HDMI prête à l'emploi</strong></span>
                </li>
              </ul>
              <p className="mt-3">Notre support reste disponible pour vous guider à chaque étape.</p>
            </div>
          )
        },
        {
          q: "Que faire si ça ne fonctionne pas ?",
          a: "Notre équipe support est là pour vous aider. Contactez-nous via votre espace client ou par email. Nous répondons rapidement et trouvons toujours une solution."
        }
      ]
    },
    {
      icon: <Shield className="h-6 w-6" />,
      title: 'Compte & abonnement',
      color: '#00FF88',
      questions: [
        {
          q: "Comment accéder à mon abonnement ?",
          a: (
            <div>
              <p className="mb-3">Après votre commande :</p>
              <ul className="space-y-2 ml-4">
                <li className="flex items-start space-x-2">
                  <span className="text-[#00FF88]">1.</span>
                  <span>Vous recevez vos accès par email</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#00FF88]">2.</span>
                  <span>Vous disposez d'un <strong>espace client dédié</strong></span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#00FF88]">3.</span>
                  <span>Vous pouvez gérer votre abonnement simplement</span>
                </li>
              </ul>
            </div>
          )
        },
        {
          q: "L'abonnement est-il renouvelé automatiquement ?",
          a: "L'abonnement est annuel. Les modalités de renouvellement sont clairement indiquées dans votre espace client, sans surprise."
        },
        {
          q: "Puis-je offrir l'abonnement à un proche ?",
          a: "Oui, absolument ! IPTV4KFR est parfaitement adapté à une utilisation familiale ou comme cadeau. C'est une excellente façon de faire plaisir tout en partageant des moments de divertissement."
        },
        {
          q: "Comment résilier ou modifier mon abonnement ?",
          a: "Contactez notre support via votre espace client. Nous traitons toutes les demandes rapidement et simplement."
        }
      ]
    },
    {
      icon: <Heart className="h-6 w-6" />,
      title: 'Parrainage – Gagnez avec IPTV4KFR',
      color: '#FF6B9D',
      questions: [
        {
          q: "Qu'est-ce que le parrainage IPTV4KFR ?",
          a: "Le parrainage vous permet de recommander IPTV4KFR à votre entourage et de bénéficier de crédits ou d'avantages lorsqu'un proche s'abonne via votre lien. Plus vous partagez, plus vous gagnez !"
        },
        {
          q: "Comment fonctionne le parrainage ?",
          a: (
            <div>
              <ul className="space-y-2">
                <li className="flex items-start space-x-2">
                  <span className="text-[#FF6B35]">1.</span>
                  <span>Vous partagez votre lien personnel (disponible dans votre espace)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#FF6B35]">2.</span>
                  <span>Votre filleul s'abonne via ce lien</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#FF6B35]">3.</span>
                  <span>Vous êtes automatiquement crédité</span>
                </li>
              </ul>
              <p className="mt-3 text-[#00FF88]">Tout est visible et suivi dans votre espace personnel, en toute transparence.</p>
            </div>
          )
        },
        {
          q: "À quoi servent les crédits de parrainage ?",
          a: (
            <div>
              <p className="mb-3">Les crédits peuvent être utilisés pour :</p>
              <ul className="space-y-2 ml-4">
                <li className="flex items-start space-x-2">
                  <span className="text-[#00FF88]">✓</span>
                  <span>Réduire le coût de votre prochain abonnement</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#00FF88]">✓</span>
                  <span>Prolonger votre accès gratuitement</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#00FF88]">✓</span>
                  <span>Bénéficier d'avantages liés à la plateforme</span>
                </li>
              </ul>
            </div>
          )
        },
        {
          q: "Le parrainage est-il limité ?",
          a: "Non. Plus vous recommandez, plus vous cumulez d'avantages. C'est un système simple, transparent et motivant. Certains de nos membres ont réussi à rendre leur abonnement 100% gratuit grâce au parrainage !"
        }
      ]
    },
    {
      icon: <TrendingUp className="h-6 w-6" />,
      title: 'Vendeurs – Rejoignez la team',
      color: '#FFB800',
      questions: [
        {
          q: "Qui peut devenir vendeur IPTV4KFR ?",
          a: "Toute personne motivée, disposant d'un réseau ou d'une audience, et souhaitant générer des revenus récurrents en recommandant une offre attractive et familiale."
        },
        {
          q: "Que propose IPTV4KFR aux vendeurs ?",
          a: (
            <div>
              <ul className="space-y-2">
                <li className="flex items-start space-x-2">
                  <span className="text-[#FFB800]">✓</span>
                  <span>Un <strong>espace vendeur dédié</strong> avec tableau de bord complet</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#FFB800]">✓</span>
                  <span>Un suivi clair des ventes et commissions en temps réel</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#FFB800]">✓</span>
                  <span>Des outils simples à utiliser (liens de paiement, codes vendeur)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#FFB800]">✓</span>
                  <span>Une offre facile à présenter (plusieurs durées, à partir de {pricing.fromPrice}€)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#FFB800]">✓</span>
                  <span>Support et formation pour réussir</span>
                </li>
              </ul>
            </div>
          )
        },
        {
          q: "Faut-il des compétences techniques ?",
          a: "Non. Le vendeur se concentre sur la recommandation. La plateforme, l'accès client et le support technique sont entièrement gérés par IPTV4KFR. Vous vous concentrez sur la vente, nous gérons le reste."
        },
        {
          q: "Comment sont calculées les commissions ?",
          a: "Les commissions sont automatiquement calculées et visibles dans l'espace vendeur, avec un suivi clair des gains et des paiements. Tout est transparent : vous voyez en temps réel vos ventes, vos commissions disponibles et vos paiements."
        },
        {
          q: "Pourquoi rejoindre IPTV4KFR plutôt qu'une autre plateforme ?",
          a: (
            <div>
              <ul className="space-y-2">
                <li className="flex items-start space-x-2">
                  <span className="text-[#00FF88]">✓</span>
                  <span><strong>Offre simple et lisible</strong> : plusieurs durées, à partir de {pricing.fromPrice}€</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#00FF88]">✓</span>
                  <span><strong>Forte demande</strong> : sport, TV, films, séries pour toute la famille</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#00FF88]">✓</span>
                  <span><strong>Positionnement premium</strong> avec une image professionnelle</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#00FF88]">✓</span>
                  <span><strong>Système structuré et sérieux</strong> : paiements fiables, support réactif</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#00FF88]">✓</span>
                  <span><strong>Vision long terme</strong> : plateforme en développement constant</span>
                </li>
              </ul>
            </div>
          )
        }
      ]
    },
    {
      icon: <Users className="h-6 w-6" />,
      title: 'Support & accompagnement',
      color: '#00FFFF',
      questions: [
        {
          q: "Que faire en cas de problème ?",
          a: "Un support est disponible pour vous accompagner. Notre objectif : une expérience fluide, sans stress. Contactez-nous et nous trouvons toujours une solution ensemble."
        },
        {
          q: "Où trouver de l'aide ?",
          a: (
            <div>
              <ul className="space-y-2">
                <li className="flex items-start space-x-2">
                  <span className="text-[#FF6B35]">📚</span>
                  <span><Link to="/tutoriels" className="text-[#FF6B35] hover:underline">Section Tutoriels et assistance</Link> avec guides détaillés</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#FF6B35]">👤</span>
                  <span><Link to="/client" className="text-[#FF6B35] hover:underline">Espace client</Link> pour gérer votre compte</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-[#FF6B35]">💬</span>
                  <span>Support dédié (email et Telegram)</span>
                </li>
              </ul>
            </div>
          )
        },
        {
          q: "Le support est-il rapide ?",
          a: "Oui. Nous nous engageons à répondre rapidement à toutes vos questions. Notre équipe est disponible 24/7 pour vous accompagner."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen py-20" data-testid="faq-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="inline-block p-4 bg-[#FF6B35]/10 rounded-full mb-6">
            <HelpCircle className="h-12 w-12 text-[#FF6B35]" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Questions fréquentes
          </h1>
          <p className="text-base sm:text-lg text-[#B8B8BA] max-w-3xl mx-auto">
            Trouvez toutes les réponses à vos questions sur IPTV4KFR
          </p>
        </motion.div>

        {/* FAQ Categories */}
        <div className="space-y-12">
          {faqCategories.map((category, catIndex) => (
            <motion.div
              key={catIndex}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * catIndex }}
            >
              <div className="flex items-center space-x-3 mb-6">
                <div style={{ color: category.color }}>
                  {category.icon}
                </div>
                <h2 className="text-2xl font-bold text-white">
                  {category.title}
                </h2>
              </div>

              <div className="space-y-4">
                {category.questions.map((item, qIndex) => {
                  const globalIndex = `${catIndex}-${qIndex}`;
                  const isOpen = openQuestion === globalIndex;

                  return (
                    <div
                      key={qIndex}
                      className="bg-gradient-to-br from-[#1A1A1D] to-[#121214] rounded-xl border border-white/10 overflow-hidden"
                    >
                      <button
                        onClick={() => toggleQuestion(globalIndex)}
                        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-[#222226]/50 transition-all duration-300"
                      >
                        <span className="text-white font-semibold pr-4">
                          {item.q}
                        </span>
                        {isOpen ? (
                          <ChevronUp className="h-5 w-5 text-[#FF6B35] flex-shrink-0" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-[#B8B8BA] flex-shrink-0" />
                        )}
                      </button>

                      {isOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                          className="px-6 pb-6"
                        >
                          <div className="pt-2 border-t border-[#3A3A3D]">
                            <div className="text-[#E8E8E8] leading-relaxed">
                              {typeof item.a === 'string' ? item.a : item.a}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </div>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Message final */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-16 bg-gradient-to-br from-[#FF6B35] to-[#F7931E] p-10 rounded-xl text-center"
        >
          <h3 className="text-3xl font-bold text-white mb-4">
            IPTV4KFR, c'est plus qu'un abonnement
          </h3>
          <p className="text-white text-lg mb-8 max-w-2xl mx-auto">
            C'est une expérience pensée pour les utilisateurs, les parrains et les vendeurs. 
            Une solution complète pour toute la famille, à un prix qui change tout.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/offre"
              className="bg-white text-[#FF6B35] font-semibold px-8 py-3 rounded-lg hover:bg-gray-100 transition-all duration-300 inline-block"
            >
              Découvrir l'offre
            </Link>
            <Link
              to="/client"
              className="bg-[#1A1A1D] text-white font-semibold px-8 py-3 rounded-lg border border-white/20 hover:border-white/40 transition-all duration-300 inline-block"
            >
              Accéder à mon espace
            </Link>
          </div>
        </motion.div>

        {/* Support info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-12 text-center"
        >
          <p className="text-[#B8B8BA] mb-2">
            Vous ne trouvez pas la réponse à votre question ?
          </p>
          <p className="text-white">
            📞 Notre équipe support est là pour vous • <span className="text-[#FF6B35]">24/7</span>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default FAQ;
